<div class="header">
  <button class="close-button" [mat-dialog-close]="undefined" type="button" tabindex="-1" mat-icon-button
    aria-label="Close">
    <mat-icon class="notranslate">clear</mat-icon>
  </button>
  <div class="title">
    <mat-icon class="notranslate" svgIcon="get-cash" />
    <p>{{ data.title }}</p>
  </div>
</div>

<div class="content terra-error-style">
  <mat-stepper linear labelPosition="bottom" #stepper>
    <!-- Edit icon overrides -->
    <ng-template matStepperIcon="edit">
      <mat-icon class="notranslate">adjust</mat-icon>
    </ng-template>
    <!-- Amount step -->
    <mat-step [stepControl]="amountForm" #step1 state="amount" [completed]="hasAmount">
      <!-- set label -->
      <ng-template matStepLabel>
        <span class="step-label">Amount</span>
      </ng-template>
      <!-- Custom icon -->
      <ng-template matStepperIcon="amount">
        <mat-icon class="notranslate">{{ getIconName(step1) }}</mat-icon>
      </ng-template>
      <form [formGroup]="amountForm">
        <!-- Loader -->
        @if (data.displayAchDebitLimits && !(bankAccountLimits$ | async)) {
        <terra-loader />
        } @else {
        <!-- Amount input -->
        <div class="terra-form-field-wrapper full-width transfer-amount-field">
          <label for="transferAmount" class="text-input-label">Transfer Amount</label>
          <mat-form-field appearance="outline" class="half-width">
            <input type="tel" matInput terraFormattedNumber id="transferAmount" formControlName="amount"
              class="lo_sensitive" placeholder="1,150,000" />
            <span matPrefix>$</span>
            <ng-container ngProjectAs="mat-error">
              @for (error of amountErrors; track error.errorName) {
                @if (amountForm.get('amount').hasError(error.errorName)) {
                  <mat-error>{{error.description}}</mat-error>
                }
              }
            </ng-container>
          </mat-form-field>
        </div>
        }
        <span class="actions">
          <button mat-flat-button [disabled]="!hasAmount" class="next-button amount-next cov-button" type="button"
            matStepperNext>{{
            data.amountStepButtonText }}</button>
          <button mat-stroked-button class="back-button cov-button" type="button"
            [matDialogClose]="undefined">CANCEL</button>
        </span>
      </form>
    </mat-step>
    <!-- Bank Account step -->
    <mat-step [stepControl]="bankAccountForm" #step2 state="account"
      [completed]="(canMakeTransfer$ | async) && !(insufficientAmount$ | async)">
      <!-- set label -->
      <ng-template matStepLabel>
        <span class="step-label">Select Bank Account</span>
      </ng-template>
      <!-- Custom icon -->
      <ng-template matStepperIcon="account">
        <mat-icon class="notranslate">{{ getIconName(step2) }}</mat-icon>
      </ng-template>
      <form [formGroup]="bankAccountForm" class="transfer-plaid-bank-account-form">
        <!-- Loader -->
        @if (isLoadingBankAccounts$ | async) {
          <terra-loader />
        } @else {
        @if (linkedBankAccountsGroup$ | async; as linkedAccounts) {
        <ng-container>
          @if (linkedAccounts?.length) {
          <p class="step-message">
            Choose a connected bank account
          </p>
          <p-dropdown [options]="linkedAccounts.length > 1 ? linkedAccounts : linkedAccounts[0]?.items"
            formControlName="bankAccount" optionLabel="nickname" [filter]="true" filterBy="nickname"
            placeholder="Select" styleClass="w-full" [group]="linkedAccounts.length > 1"
            (onChange)="accountSelected($event)">
            @if (linkedAccounts.length > 1) {
            <ng-template let-group pTemplate="group">
              @if (group.name == 'Last') {
              <hr class="separator">
              }
            </ng-template>
            }
            <ng-template let-account pTemplate="item">
              @if (account.verificationStatus === autoVerificationPendingStatus || 
                   account.verificationStatus === manualVerificationPendingStatus) {
                {{account.nickname}} <span class="text-xs text-orange-300">
                  Pending: {{account.tokenCreatedAt | date : "yyyy/MM/dd"}}
                </span> 
              } @else {
                {{account.nickname}}
              }
            </ng-template>
            <ng-template pTemplate="filter" let-options="options">
              <terra-search-field class="search-field" placeholder="Search"
                (filterChange)="customFilterFunction($event, options)" (clearSearch)="resetFunction(options)"
                [(filter)]="filterValue" />
            </ng-template>
          </p-dropdown>
          }
          @if (bankAccountForm.get('bankAccount').value)
          {
          <p class="step-message flex gap-2 align-items-center">
            Balance:
            @if(isLoadingBalance$ | async) {
              <terra-loader class="m-0" />
            } @else if (!(isLoadingBalanceHasError$ | async)) {
              @if (currentAccountBalance$ | async; as balance) {
              <span>${{balance | terraNumber}}</span>
              } @else {
                <span class="text-orange-500 text-sm">Please ensure the account has sufficient balance.</span>
              }
            } @else {
            <mat-error>{{loadingBalanceError$ | async}}</mat-error>
            }
          </p>
          @if (!shouldNotAskForHolderName.includes(bankAccountForm.get('bankAccount').value.verificationStatus)) {
          <p class="step-message">
            Account Holder’s Name
          </p>
          <div class="terra-form-field-wrapper full-width transfer-amount-field mb-0">
            <mat-form-field appearance="outline" class="half-width">
              <input type="text" matInput formControlName="accountHolder" class="lo_sensitive" />
              @if (bankAccountForm.get('accountHolder').hasError('required')) {
              <mat-error>Required</mat-error>
              }
            </mat-form-field>
          </div>
          <span class="text-gray-500 text-xs">The name provided (whether an individual or a business) must match
            the
            name on your account in order to
            verify this account.</span>
          }
          }
          @if (insufficientAmount$ | async) {
          <p class="text-red-500 text-lg">
            The requested amount {{amountForm.get('amount').value | terraNumber}} exceeds your account balance of
            {{(currentAccountBalance$ | async) | terraNumber}}
          </p>
          }

          <!-- Link new account -->
          <div class="border">
            <button type="button" class="plaid-link-button" mat-button (click)="openPlaidLink()">
              <mat-icon class="notranslate">add</mat-icon>
              {{ linkedAccounts?.length > 0 ? 'Connect a different bank account' : 'Connect an external bank account'
              }}
            </button>
            @if (hasErrorReceivingLinkToken$ | async) {
              <mat-error>An error has occurred, please try again or contact support </mat-error>
            }
          </div>
        </ng-container>
        }
        @if (hasErrorReceivingLinkedBankAccounts$ | async) {
          <mat-error> An error occurred getting linked bank accounts, please try again or contact support </mat-error>
        }
        }
        <span class="actions">
          <button mat-flat-button class="next-button account-next cov-button" type="button" matStepperNext
            [disabled]="(disabledConfirmButton$ | async) || !(canMakeTransfer$ | async) || (insufficientAmount$ | async)">
            CONFIRM
          </button>
          <button mat-stroked-button class="back-button cov-button" type="button" matStepperPrevious>BACK</button>
        </span>
      </form>
    </mat-step>
    <!-- Confirmation step -->
    <mat-step #step3 state="confirmation" [completed]="false">
      <!-- set label -->
      <ng-template matStepLabel>
        <span class="step-label">Confirm Transfer</span>
      </ng-template>
      <!-- Custom icon -->
      <ng-template matStepperIcon="confirmation">
        <mat-icon class="notranslate">{{ getIconName(step3) }}</mat-icon>
      </ng-template>
      @if (!!amountForm.get('amount').value && !!bankAccountForm.get('bankAccount').value) {
      <ng-container>
        <p class="step-message">
          Do you want to proceed with this transfer?
        </p>
        <!-- Transfer summary -->
        <div class="summary">
          <div class="left-side">
            <span>From:</span>
            <span>To:</span>
            <span>Amount:</span>
          </div>
          <div class="right-side">
            <span>{{ bankAccountForm.get('bankAccount').value.nickname }}</span>
            <span>{{ data.unitBankAccountName }}</span>
            <span>${{ amountForm.get('amount').value | terraNumber }}</span>
          </div>
        </div>
      </ng-container>
      }
      <!-- Loader -->
      @if (isSubmitting$ | async) { <terra-loader /> }
      @if (hasErrorSubmittingTransfer$ | async) {
      <mat-error class="plaid-error-wrapper">
        {{ submitErrorText || 'An error has occurred, please try again or contact support' }}
      </mat-error>
      }
      @if (insufficientAmount$ | async) {
        <p class="text-red-500 text-lg">
          The requested amount {{amountForm.get('amount').value | terraNumber}} exceeds your account balance of
          {{(currentAccountBalance$ | async) | terraNumber}}
        </p>
      }
      <span class="actions">
        <button mat-flat-button class="next-button confirmation-submit cov-button" type="button"
          [disabled]="!(canMakeTransfer$ | async) || (isSubmitting$ | async) || (insufficientAmount$ | async)"
          (click)="fundAccount()">
          MAKE TRANSFER
        </button>
        <button mat-stroked-button class="back-button cov-button" type="button" [disabled]="isSubmitting$ | async"
          matStepperPrevious>
          BACK
        </button>
      </span>
    </mat-step>
  </mat-stepper>
</div>